/**
 * PillarTabs — the sticky line-tab strip shared by the pillar pages.
 *
 * Generic over a `TabId` string-union so each page passes its own tab set
 * (e.g. `'drifts' | 'pr-tracker' | 'export'` for Baseline Compliance,
 * `'overview' | 'mfa' | 'licenses' | 'inactive' | 'all'` for User Analytics).
 *
 * URL-syncs the active tab via a configurable search param (default: `tab`).
 * The default tab elides from the URL to keep canonical links clean.
 *
 * Visual: Carbon line tabs (components/carbon/LineTabs) in tab mode. The
 * selected tab is the explicit value from `usePillarTab`, never router
 * matching. Tab ids stay `tab-{id}` and control `panel-{id}`, the pages'
 * existing tabpanels. The strip pins at the top of <main> on the ground
 * color and publishes its height as `--pillar-tabs-h`.
 *
 * Reference pattern: `Dashboard/v2/PostureTabs.tsx` (Command Center).
 */

import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { PillarTone } from '@/components/Dashboard/v2/PillarShell'
import { LineTabs } from '@/components/carbon/LineTabs'

export interface TabConfig<TabId extends string> {
  id: TabId
  label: string
  /** Count shown as a small tag after the label. */
  badge?: string | number | null
}

interface Props<TabId extends string> {
  /** Ordered list of tabs to render. */
  tabs: ReadonlyArray<TabConfig<TabId>>
  /** Tab id used when the URL has no `?tab=…`. Elided from the URL. */
  defaultTab: TabId
  /** The pillar. Kept for the frozen interface; line tabs are neutral. */
  tone: PillarTone
  /** Right-side actions slot (filter, new-scan, threshold buttons). Optional. */
  actions?: React.ReactNode
  /** URL search param name. Defaults to `tab` to match the existing pages. */
  paramKey?: string
  /** Optional aria-label for the tablist. Defaults to "Posture view". */
  ariaLabel?: string
  /**
   * Where the strip sits. `below-brief` (default): 48px under the brief, 32px over the panel.
   * `above-brief`: page sub-navigation directly under the breadcrumb, 16px down, with the
   * brief's own 32px top margin below it (mockup 03).
   */
  placement?: 'above-brief' | 'below-brief'
}

const PLACEMENT = { 'below-brief': 'mb-8 mt-12', 'above-brief': 'mt-4' } as const

/**
 * Hook variant of the URL sync — exposed so pages can both read the active
 * tab and drive their per-tab content rendering from a single source of
 * truth. Matches the shape of `useDashboardTab` in PostureTabs.tsx.
 */
export function usePillarTab<TabId extends string>(
  validTabs: ReadonlyArray<TabId>,
  defaultTab: TabId,
  paramKey: string = 'tab',
): [TabId, (next: TabId) => void] {
  const [params, setParams] = useSearchParams()
  const raw = params.get(paramKey)

  // Validate against the page's own tab set so a stale or hand-crafted URL
  // can't put us into an unknown tab id. Falls back to the default.
  const current: TabId = useMemo(() => {
    if (raw && (validTabs as ReadonlyArray<string>).includes(raw)) return raw as TabId
    return defaultTab
  }, [raw, validTabs, defaultTab])

  const setTab = useCallback(
    (next: TabId) => {
      setParams(prev => {
        if (next === defaultTab) prev.delete(paramKey)
        else prev.set(paramKey, next)
        return prev
      }, { replace: true })
    },
    [defaultTab, paramKey, setParams],
  )

  return [current, setTab]
}

export default function PillarTabs<TabId extends string>({
  tabs,
  defaultTab,
  actions,
  paramKey = 'tab',
  ariaLabel = 'Posture view',
  placement = 'below-brief',
}: Props<TabId>) {
  const validIds = useMemo(() => tabs.map(t => t.id), [tabs])
  const [current, setTab] = usePillarTab(validIds, defaultTab, paramKey)
  const items = useMemo(() => tabs.map(t => ({ id: t.id, label: t.label, count: t.badge })), [tabs])

  const stripRef = useRef<HTMLDivElement>(null)

  // Publish this sticky strip's rendered height to a CSS variable so an
  // in-page sticky element below it (the BPA tab's SectionJumpBar) can pin
  // flush beneath the strip instead of colliding with it at top:0. Kept
  // current with a ResizeObserver so it survives the actions row wrapping
  // below 672px and font reflow. offsetHeight is the border-box height and
  // excludes the margins, so it's exactly the strip's bottom edge when
  // pinned at top:0.
  useEffect(() => {
    const el = stripRef.current
    if (typeof document === 'undefined' || !el) return
    const root = document.documentElement
    const publish = () => {
      root.style.setProperty('--pillar-tabs-h', `${el.offsetHeight}px`)
    }
    publish()
    let ro: ResizeObserver | undefined
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(publish)
      ro.observe(el)
    }
    return () => {
      ro?.disconnect()
      root.style.removeProperty('--pillar-tabs-h')
    }
  }, [])

  return (
    <div ref={stripRef} className={`sticky top-0 z-20 bg-[color:var(--background)] ${PLACEMENT[placement]}`}>
      <LineTabs
        aria-label={ariaLabel}
        items={items}
        value={current}
        onChange={id => setTab(id as TabId)}
        actions={actions}
      />
    </div>
  )
}
